import { money, paidOf } from '../../api/plannerApi';
import { BACKLOG } from '../../types/board';
import type { Board, Category } from '../../types/board';

// Backlog items are candidates, not yet part of the budget — exclude from totals.
const budgeted = (items: Board['items']) => items.filter((i) => i.status !== BACKLOG);

export default function Header({
  board,
  onTitleChange,
  activeCategoryId,
  onToggleFilter,
  onAddCategory,
  onEditCategory,
  onDeleteCategory,
  search,
  onSearchChange,
}: {
  board: Board;
  onTitleChange: (title: string) => void;
  activeCategoryId: string | null;
  onToggleFilter: (id: string) => void;
  onAddCategory: () => void;
  onEditCategory: (cat: Category) => void;
  onDeleteCategory: (cat: Category) => void;
  search: string;
  onSearchChange: (v: string) => void;
}) {
  const items = budgeted(board.items);
  const expected = items.reduce((a, i) => a + (i.expectedPrice || 0), 0);
  const paid = items.reduce((a, i) => a + paidOf(i), 0);
  const pct = expected > 0 ? Math.min(100, Math.round((paid / expected) * 100)) : 0;

  return (
    <div className="mb-8 border border-line bg-card px-4 sm:px-10 py-7 sm:py-9">
      <div className="font-display italic text-5xl leading-none mb-2">Budget</div>
      <input
        value={board.title}
        onChange={(e) => onTitleChange(e.target.value)}
        className="font-serif text-xl text-muted bg-transparent border-none border-b border-dashed border-transparent outline-none w-full max-w-md hover:border-line focus:border-terra"
      />

      <div className="mt-7 grid grid-cols-1 sm:grid-cols-3 border-t border-line pt-7 text-center gap-y-4 sm:gap-y-0 sm:divide-x divide-line">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-muted mb-1">Expected</div>
          <div className="font-serif text-2xl sm:text-[27px] tabular-nums">{money(expected)}</div>
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-muted mb-1">Paid</div>
          <div className="font-serif text-2xl sm:text-[27px] tabular-nums text-sage">{money(paid)}</div>
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-muted mb-1">Remaining</div>
          <div className="font-serif text-2xl sm:text-[27px] tabular-nums">{money(Math.max(0, expected - paid))}</div>
        </div>
      </div>

      <div className="mt-6">
        <div className="h-2 rounded-full bg-[oklch(92%_0.012_60)] overflow-hidden">
          <div className="h-full rounded-full bg-sage" style={{ width: `${pct}%` }} />
        </div>
        <div className="mt-1.5 text-xs text-muted text-right">{pct}% paid</div>
      </div>

      <div className="mt-7">
        <input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          onDrop={(e) => e.preventDefault()}
          placeholder="Search items by name, vendor, or notes…"
          className="w-full max-w-[420px] border border-line bg-cream px-3.5 py-2.5 text-sm outline-none focus:border-terra"
        />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2.5">
        {board.categories.map((cat) => {
          const items = budgeted(board.items).filter((i) => i.categoryIds.includes(cat.id));
          const expectedC = items.reduce((a, i) => a + (i.expectedPrice || 0), 0);
          const paidC = items.reduce((a, i) => a + paidOf(i), 0);
          const p = expectedC > 0 ? Math.round((paidC / expectedC) * 100) : 0;
          const active = activeCategoryId === cat.id;
          return (
            <div
              key={cat.id}
              className="flex items-center gap-1.5 rounded-full px-3.5 py-[7px] text-sm font-semibold"
              style={{
                border: `1.5px solid ${active ? 'oklch(50% 0.1 20)' : 'oklch(88% 0.015 60)'}`,
                background: active ? 'oklch(50% 0.1 20)' : 'transparent',
                color: active ? 'oklch(99% 0.006 75)' : 'oklch(24% 0.02 50)',
              }}
            >
              <span onClick={() => onToggleFilter(cat.id)} className="cursor-pointer select-none">
                {cat.name} <span className="opacity-65 font-normal">· {p}%</span>
              </span>
              <button
                onClick={() => onEditCategory(cat)}
                title={`Rename ${cat.name}`}
                aria-label={`Rename ${cat.name}`}
                className="opacity-70 hover:opacity-100"
              >
                ✎
              </button>
              <button
                onClick={() => onDeleteCategory(cat)}
                title={`Delete ${cat.name}`}
                aria-label={`Delete ${cat.name}`}
                className="text-[14px] leading-none opacity-70 hover:opacity-100"
              >
                ×
              </button>
            </div>
          );
        })}
        <button
          onClick={onAddCategory}
          className="rounded-full px-3.5 py-[7px] text-sm border-[1.5px] border-dashed border-line text-muted hover:border-terra hover:text-terra"
        >
          + Category
        </button>
      </div>
    </div>
  );
}
